import { useEffect, useState } from 'react';
import Layout from '@/components/Layout';
import axios from 'axios';
import { Calendar, User, Users, ExternalLink } from 'lucide-react';
import BookingModal from '@/components/BookingModal';
import OneOnOneBookingModal from '@/components/OneOnOneBookingModal';
import { useHeroImage } from '@/hooks/useHeroImage';
import HeroImageEditor from '@/components/HeroImageEditor';
import EditableText from '@/components/EditableText';
import PageIcon from '@/components/PageIcon';
import PageQuote from '@/components/PageQuote';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function InternalPractices() {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showBookingModal, setShowBookingModal] = useState(false);
  const [showOneOnOneModal, setShowOneOnOneModal] = useState(false);
  const heroImage = useHeroImage('internal_practices');

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      const response = await axios.get(`${API}/internal-practices-settings`);
      setSettings(response.data);
    } catch (error) {
      console.error('Failed to load settings:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout>
      <section className="relative">
        <HeroImageEditor pageName="internal_practices" />
        <div className="relative bg-gradient-to-br from-[#3D2817] via-[#2C1810] to-[#3D2817] py-20">
          {heroImage && (
            <div 
              className="absolute inset-0 bg-cover bg-center opacity-20"
              style={{ backgroundImage: `url(${heroImage})` }}
            />
          )}
          <div className="absolute inset-0 bg-gradient-to-r from-transparent via-[#E8B67A] to-transparent opacity-30 z-20"></div>
          
          <div className="relative z-30 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center">
              <PageIcon pageName="internal_practices" size="lg" className="mx-auto mb-6" />
              <h1 className="text-5xl sm:text-6xl md:text-7xl font-bold retro-title text-[#F4E4C1] mb-6 drop-shadow-[0_4px_8px_rgba(0,0,0,0.5)]">
                <EditableText as="span" id="internal_practices.hero.title">Internal Practices</EditableText>
              </h1>
              <p className="text-xl md:text-2xl text-[#F4E4C1] max-w-3xl mx-auto font-medium">
                <EditableText as="span" id="internal_practices.hero.subtitle">Exclusive training content for clinic practitioners</EditableText>
              </p>
            </div>
          </div>
        </div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">

          {/* Quote Position 1 - Above Main Content */}
          <PageQuote pageName="internal_practices" position={1} variant="compact" />

          {loading ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#FFA500]"></div>
            </div>
          ) : settings ? (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
              {/* Private Session Card */}
              <div className="cassette-card overflow-hidden relative hover:shadow-2xl transition-shadow group">
                {settings.private_session.image_url && (
                  <>
                    <div 
                      className="absolute inset-0 bg-cover bg-center"
                      style={{ backgroundImage: `url(${settings.private_session.image_url})` }}
                    />
                    <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/60 to-black/80"></div>
                  </>
                )}
                <div className="relative z-10 p-8 text-center">
                  <Calendar className={`w-16 h-16 mx-auto mb-4 ${settings.private_session.image_url ? 'text-[#E8B67A]' : 'text-[#E85D4D]'}`} />
                  <h3 className={`text-2xl retro-title mb-3 ${settings.private_session.image_url ? 'text-white' : 'text-[#2C1810]'}`}>Private Session</h3>
                  <p className={`mb-6 ${settings.private_session.image_url ? 'text-white' : 'text-gray-700'}`}>{settings.private_session.description}</p>
                  {settings.private_session.is_clickable ? (
                    <button
                      onClick={() => setShowBookingModal(true)}
                      className="retro-button w-full"
                    >
                      {settings.private_session.button_text}
                    </button>
                  ) : (
                    <div className="px-4 py-2 bg-gray-200 text-gray-600 rounded-lg font-semibold border-2 border-gray-400">
                      {settings.private_session.button_text}
                    </div>
                  )}
                </div>
              </div>

              {/* One-on-One Consultation Card */}
              <div className="cassette-card overflow-hidden relative hover:shadow-2xl transition-shadow group">
                {settings.one_on_one.image_url && (
                  <>
                    <div 
                      className="absolute inset-0 bg-cover bg-center"
                      style={{ backgroundImage: `url(${settings.one_on_one.image_url})` }}
                    />
                    <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/60 to-black/80"></div>
                  </>
                )}
                <div className="relative z-10 p-8 text-center">
                  <User className={`w-16 h-16 mx-auto mb-4 ${settings.one_on_one.image_url ? 'text-[#E8B67A]' : 'text-[#4D9E7C]'}`} />
                  <h3 className={`text-2xl retro-title mb-3 ${settings.one_on_one.image_url ? 'text-white' : 'text-[#2C1810]'}`}>One-on-One Consultation</h3>
                  <p className={`mb-6 ${settings.one_on_one.image_url ? 'text-white' : 'text-gray-700'}`}>{settings.one_on_one.description}</p>
                  {settings.one_on_one.is_clickable ? (
                    <button
                      onClick={() => setShowOneOnOneModal(true)}
                      className="retro-button w-full"
                    >
                      {settings.one_on_one.button_text}
                    </button>
                  ) : (
                    <div className="px-4 py-2 bg-gray-200 text-gray-600 rounded-lg font-semibold border-2 border-gray-400">
                      {settings.one_on_one.button_text}
                    </div>
                  )}
                </div>
              </div>

              {/* Free Group Study Card */}
              <div className="cassette-card overflow-hidden relative hover:shadow-2xl transition-shadow group">
                {settings.free_group_study.image_url && (
                  <>
                    <div 
                      className="absolute inset-0 bg-cover bg-center"
                      style={{ backgroundImage: `url(${settings.free_group_study.image_url})` }}
                    />
                    <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/60 to-black/80"></div>
                  </>
                )}
                <div className="relative z-10 p-8 text-center">
                  <Users className={`w-16 h-16 mx-auto mb-4 ${settings.free_group_study.image_url ? 'text-[#E8B67A]' : 'text-[#FFA500]'}`} />
                  <h3 className={`text-2xl retro-title mb-3 ${settings.free_group_study.image_url ? 'text-white' : 'text-[#2C1810]'}`}>Free Group Study</h3>
                  <p className={`mb-6 ${settings.free_group_study.image_url ? 'text-white' : 'text-gray-700'}`}>{settings.free_group_study.description}</p>
                  {settings.free_group_study.is_clickable && settings.free_group_study.zoom_link ? (
                    <a
                      href={settings.free_group_study.zoom_link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="retro-button w-full inline-flex items-center justify-center gap-2"
                    >
                      {settings.free_group_study.button_text}
                      <ExternalLink className="w-4 h-4" />
                    </a>
                  ) : (
                    <div className="px-4 py-2 bg-gray-200 text-gray-600 rounded-lg font-semibold border-2 border-gray-400">
                      {settings.free_group_study.button_text}
                    </div>
                  )}
                </div>
              </div>
            </div>
          ) : null}

          {/* Quote Position 2 - Below Main Content */}
          <PageQuote pageName="internal_practices" position={2} variant="compact" />
        </div>
      </section>

      {/* Modals */}
      {showBookingModal && settings && (
        <BookingModal 
          service={{
            id: 'private_session',
            name: 'Private Session',
            description: settings.private_session.description,
            duration_minutes: 45,
            price: 100
          }}
          onClose={() => setShowBookingModal(false)} 
        />
      )}

      {showOneOnOneModal && (
        <OneOnOneBookingModal onClose={() => setShowOneOnOneModal(false)} />
      )}
    </Layout>
  );
}
